import moment from 'moment';
import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';

import { Title } from 'components/Common';
import { Button, Input } from '@cloudez/cloudez-design-system';
import { Alert } from 'components/Alert';

import history from 'services/history';
import toastError from 'utils/toastError';
import { toast } from 'react-toastify';
import {
  getPixService,
  getInvoiceByHashService,
  refreshPixExternalService,
} from 'services/invoice';
import {
  Wrapper,
  QRCodeContainer,
  PixContainer,
  CopyButton,
  SpinnerIcon,
  RemainingTime,
} from './styles';
import PixIcon from 'assets/img/svg/pix.svg';
import PixIconNegative from 'assets/img/svg/pix-negative.svg';

interface PixData {
  qrcode: string;
  qrcode_text: string;
  qrcode_expires_at: string;
  total: string;
}

const InvoicePix: React.FC = () => {
  const theme = useContext(ThemeContext);
  const { hash } = useParams<{ hash: string }>();

  const [pixData, setPixData] = useState<PixData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!pixData) return;
    const updateRemaining = () => {
      const diff = moment(pixData.qrcode_expires_at).diff(moment(), 'seconds');
      setRemaining(diff > 0 ? diff : 0);
    };
    updateRemaining();
    const interval = setInterval(updateRemaining, 1000);
    return () => clearInterval(interval);
  }, [pixData]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const { data } = await getInvoiceByHashService(hash);
        if (data.is_paid) {
          history.push(`/invoices/success`);
        }
      } catch (e) {}
    }, 10000);
    return () => clearInterval(interval);
  }, [hash]);

  const copyToClipboard = async () => {
    if (pixData?.qrcode_text) {
      try {
        await navigator.clipboard.writeText(pixData.qrcode_text);
        setCopied(true);
        toast.success('Código Pix copiado para a área de transferência!');

        setTimeout(() => {
          setCopied(false);
        }, 3000);
      } catch (err) {
        toast.error('Erro ao copiar código Pix');
      }
    }
  };

  const loadPixData = async () => {
    try {
      setLoading(true);
      const invoiceResp = await getInvoiceByHashService(hash);
      if (invoiceResp.data.is_paid) {
        history.push(`/invoices/${invoiceResp.data.id}`);
        return;
      }
      const { data } = await getPixService(hash);
      const isExpired =
        !data.qrcode_expires_at ||
        moment(data.qrcode_expires_at).isBefore(moment());
      if (isExpired) {
        await refreshPixExternalService(hash);
        const { data: refreshedData } = await getPixService(hash);
        setPixData(refreshedData);
      } else {
        setPixData(data);
      }
    } catch (error) {
      toastError(error);
      history.push(`/invoices/external/${hash}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hash) {
      loadPixData();
    }
  }, [hash]);

  if (loading) {
    return (
      <Wrapper>
        <div className="loading">
          <SpinnerIcon icon={faSpinner} spin size="3x" />
          <Title
            style={{
              marginTop: '20px',
            }}
          >
            Gerando código pix...
          </Title>
        </div>
      </Wrapper>
    );
  }

  if (!pixData) {
    return (
      <Wrapper>
        <div className="error">
          <p>Erro ao carregar dados do Pix</p>
          <Button onClick={() => history.push('/invoices')}>
            Voltar para Faturas
          </Button>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <PixContainer>
        <img
          src={theme.darkMode ? PixIconNegative : PixIcon}
          alt="QR Code Pix"
          width={200}
          height={120}
        />
        <h2>Aguardando pagamento...</h2>
        <p>Escaneie o QR Code ou copie o código para realizar o pagamento</p>

        <Alert info style={{ margin: '20px' }}>
          Código Pix válido até{' '}
          {new Date(pixData.qrcode_expires_at).toLocaleDateString('pt-BR')} às{' '}
          {new Date(pixData.qrcode_expires_at).toLocaleTimeString('pt-BR')}{' '}
          (Horário de Brasília)
        </Alert>
        <RemainingTime>
          {remaining > 0 ? (
            <>
              Tempo restante:{' '}
              <span>
                {String(Math.floor(remaining / 60)).padStart(2, '0')}:
                {String(remaining % 60).padStart(2, '0')}
              </span>
            </>
          ) : (
            <span style={{ color: theme.error }}>Expirado</span>
          )}
        </RemainingTime>
        <QRCodeContainer>
          {pixData.qrcode && <img src={pixData.qrcode} alt="QR Code Pix" />}
        </QRCodeContainer>

        <div className="pix-code-section">
          <p>
            <strong>
              Utilize o aplicativo do seu banco para ler o QR Code ou utilize a
              opção copiar código
            </strong>
          </p>
          <div
            className="pix-code-container"
            style={{ display: 'flex', gap: 0 }}
          >
            <Input
              block
              disabled
              background
              value={pixData.qrcode_text}
              style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            />
            <CopyButton
              onClick={copyToClipboard}
              style={{
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                marginLeft: 0,
                height: '40px',
              }}
            >
              <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
              {copied ? 'Copiado!' : 'Copiar'}
            </CopyButton>
          </div>
        </div>

        {pixData.total && (
          <div className="expiration">
            <p>
              <strong>Total: {pixData.total}</strong>
            </p>
          </div>
        )}

        <div className="actions">
          <Button
            style={{
              marginTop: '16px',
              width: '280px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
            onClick={() => history.push(`/invoices/external/${hash}`)}
          >
            Alterar forma de pagamento
          </Button>
        </div>
      </PixContainer>
    </Wrapper>
  );
};

export default InvoicePix;
